import {
  PRODUCT_CATEGORY,
  PRODUCT_CONFIG_CODE,
  PRODUCT_CONFIG_TYPE,
  PRODUCT_CONFIG_VALUE,
} from "../constants/applicationConstants";
import type { ProductConfigRules } from "../service/ProductService";

export type ExploreMarketActionInput = {
  productCategory?: string | null;
  agreementRequired?: boolean | null;
  agreementStatus?: string | null;
  agreementApproved?: boolean | null;
  productStatus?: string | null;
  productConfigs?: ProductConfigRules[] | null;
  canCreateOrder: boolean;
  canRequestAgreement?: boolean;
};

export type ExploreMarketVisibleAction = "order" | "rx" | null;

export type ExploreMarketActionFlags = {
  isAgreementFinished: boolean;
  isStandardOrGeneralCategory: boolean;
  shouldShowComingSoon: boolean;
  shouldShowRequestRx: boolean;
  isInactiveProduct: boolean;
  showAgreementButton: boolean;
  showOrderButton: boolean;
  visibleAction: ExploreMarketVisibleAction;
};

const getConfigValue = (
  productConfigs: ProductConfigRules[] | null | undefined,
  configType: string,
  configCode: string
): string | null => {
  const rule = (productConfigs || []).find(
    (r) => r.configType === configType && r.configCode === configCode
  );
  return rule?.value || null;
};

/**
 * Shared Order Product / Request Rx visibility used by Explore Market (MarketCard)
 * and Product Management. Keep this as the single source of truth for those flags.
 */
export const getExploreMarketProductActionFlags = (
  input: ExploreMarketActionInput
): ExploreMarketActionFlags => {
  const agreementRequired = !!input.agreementRequired;
  const isAgreementFinished =
    input.agreementStatus?.toUpperCase() === "EXECUTED" || !!input.agreementApproved;
  const showAgreementButton =
    agreementRequired && !isAgreementFinished && !!input.canRequestAgreement;
  const isStandardOrGeneralCategory =
    input.productCategory === PRODUCT_CATEGORY.STANDARD ||
    input.productCategory === PRODUCT_CATEGORY.GENERAL;
  const shouldShowComingSoon = isStandardOrGeneralCategory;
  const orderWorkflowType = getConfigValue(
    input.productConfigs,
    PRODUCT_CONFIG_TYPE.ORDER_WORKFLOW,
    PRODUCT_CONFIG_CODE.TYPE
  );
  const prescriptionRequirement = getConfigValue(
    input.productConfigs,
    PRODUCT_CONFIG_TYPE.ORDER_REQUIREMENT,
    PRODUCT_CONFIG_CODE.PRESCRIPTION
  );
  const shouldShowRequestRx =
    orderWorkflowType === PRODUCT_CONFIG_VALUE.OPEN_ACCESS &&
    prescriptionRequirement === PRODUCT_CONFIG_VALUE.REQUIRED;
  const isInactiveProduct = (input.productStatus || "").toUpperCase().trim() === "INACTIVE";
  const showOrderButton =
    (!agreementRequired || isAgreementFinished) &&
    !shouldShowComingSoon &&
    !isInactiveProduct &&
    input.canCreateOrder;

  const visibleAction: ExploreMarketVisibleAction = showAgreementButton
    ? null
    : showOrderButton
      ? "order"
      : shouldShowRequestRx
        ? "rx"
        : null;

  return {
    isAgreementFinished,
    isStandardOrGeneralCategory,
    shouldShowComingSoon,
    shouldShowRequestRx,
    isInactiveProduct,
    showAgreementButton,
    showOrderButton,
    visibleAction,
  };
};

/**
 * Advanced, Conservative, and Standard keep Allowable.
 * General and every other care category — including current children such as
 * GLPs, Peptides, Men Health, and Women Health, and any category added later —
 * show List Price.
 */
const ALLOWABLE_CARE_CATEGORIES = new Set([
  PRODUCT_CATEGORY.ADVANCED.toUpperCase(),
  PRODUCT_CATEGORY.CONSERVATIVE.toUpperCase(),
  PRODUCT_CATEGORY.STANDARD.toUpperCase(),
]);

export const shouldShowExploreMarketListPrice = (
  composition?: string | null,
  productCategory?: string | null
): boolean => {
  const category = (productCategory || "").trim().toUpperCase();
  if (category && !ALLOWABLE_CARE_CATEGORIES.has(category)) {
    return true;
  }

  if (!composition?.trim()) return false;
  const normalized = composition.trim().toLowerCase();
  return normalized === "glps" || normalized === "peptides" || normalized.includes("peptide");
};
